import React, { useState, lazy, Suspense } from "react";
import { Box, CircularProgress, IconButton, Modal, Paper, Stack } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import request from "src/utils/request";
import { useOrg } from "src/components/providers/org-provider";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import Flex from "src/components/flex";
import { useAuth } from "src/components/providers/auth-context";

const ModalContainer = styled(Paper)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: 85vh;
    overflow: auto;
    min-width: 45%;
`;

const MarkdownRenderer = lazy(() => import("react-markdown"));

const KnowledgebaseIconLink = ({ name, markdownId }) => {
    const { isDemo } = useAuth();
    const { org } = useOrg();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [markdown, setMarkdown] = useState("");

    const BASE_URL = org.customDocsUrl || "https://docs.adadot.com/";
    const DocumentationLinks = {
        "sprint-breakdown": BASE_URL,
        commits: BASE_URL,
        deployments: BASE_URL,
        pipelines: BASE_URL,
        "merge-requests": new URL("/Dashboard/Personal/Work/prs/", BASE_URL).href,
        reviews: BASE_URL,
        "time-of-communications": BASE_URL,
        "time-spent-communicating": BASE_URL,
        "most-used-channels": BASE_URL,
        "public-private": BASE_URL,
        "hours-in-meetings": BASE_URL,
        "meetings-breakdown": BASE_URL,
        "frequent-collaborators": BASE_URL,
        "long-hours": BASE_URL,
        fragmentation: BASE_URL,
        "out-of-hours": BASE_URL,
        "focus-time": BASE_URL,
        "time-to-review": BASE_URL,
        upskilling: BASE_URL,
        benchmarks: BASE_URL,
    };

    const getKnowledgeBaseMd = async () => {
        try {
            let response;
            if (isDemo) {
                response = (await import("src/components/knowledgebase-icon-link-mock")).default[
                    markdownId
                ];
            } else {
                response = await request(`/api/template/knowledge-base-md/${markdownId}`, {
                    method: "GET",
                });
            }
            setMarkdown(response);
        } catch (error) {
            if (error.status === 404) {
                setMarkdown(`## Document Not Found`);
            }
            console.log(error);
        }
        setLoading(false);
        return null;
    };

    const iconOnClick = async () => {
        try {
            await request(`/api/users/me/achievements`, {
                method: "POST",
                body: JSON.stringify(["visitKnowledgeBase"]),
            });
        } catch (error) {
            //
        }
    };

    const handleClose = () => {
        console.log("close");
        setOpen(false);
    };

    if (!name && !markdownId) return null;
    if (markdownId)
        return (
            <>
                <IconButton
                    size="small"
                    onClick={() => {
                        setOpen(true);
                        getKnowledgeBaseMd();
                    }}
                    aria-label="see knowledge base"
                    color="secondary"
                    target="_blank"
                >
                    <InfoIcon fontSize="small" />
                </IconButton>
                <Modal open={open} onClose={handleClose}>
                    <ModalContainer>
                        <Box p={3} position="relative" height="100%">
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                top={0}
                                width="100%"
                                alignItems="center"
                            >
                                <a href={BASE_URL} target="_blank" rel="noreferrer">
                                    Go to docs
                                </a>
                                <IconButton
                                    size="small"
                                    aria-label="close"
                                    color="inherit"
                                    onClick={handleClose}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Stack>

                            {loading ? (
                                <Flex
                                    justifyContent="center"
                                    style={{ width: "100%", height: "100%" }}
                                >
                                    <CircularProgress size={50} />
                                </Flex>
                            ) : (
                                <Suspense>
                                    <MarkdownRenderer>{markdown}</MarkdownRenderer>
                                </Suspense>
                            )}
                        </Box>
                    </ModalContainer>
                </Modal>
            </>
        );

    return (
        <IconButton
            size="small"
            href={DocumentationLinks[name] || BASE_URL}
            onClick={() => iconOnClick()}
            aria-label="see knowledge base"
            color="secondary"
            target="_blank"
        >
            <InfoIcon fontSize="small" />
        </IconButton>
    );
};

export default KnowledgebaseIconLink;
